import { useMutation, useQuery } from "@tanstack/react-query";
import {
  AccountFor,
  createAccount,
  loginApi,
  loginOtpVerification,
  loginWithOtp,
  logoutApi,
  preRegister,
  preRegisterOtpVerify,
  refreshToken,
  verifyToken,
  googleLogin,
} from "api/auth";
import AlertNotification from "components/CustomWidgets/alertNotification";
import { useAuth } from "context/AuthContext";
import { usePremium } from "context/PremiumContext";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { storeToken } from "utils/storage";
import { ref, set } from "firebase/database";
import { rtdb } from "../../firebase/firebaseChat"; // adjust path

// Login with password
export const useLogin = () => {
  const { login } = useAuth();
  const { setIsPremium } = usePremium();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: ({ payload, fcmToken }) => loginApi(payload, fcmToken),
    onSuccess: (data, variables) => {
      // Access payload through variables parameter
      const { payload } = variables;

      if (data?.data?.is_reset_password === false) {
        AlertNotification(
          "error",
          data?.message || "Please reset your password to continue.",
        );
        navigate("/forgot#reset", {
          state: {
            identifier: payload?.identifier,
            countryCode: payload?.countrycode,
          },
        });
        return;
      }

      const userData = data?.data?.user;
      if (!userData) {
        AlertNotification("error", "User data missing in response");
        return;
      }

      storeToken("accessToken", data?.data?.access);
      storeToken("refreshToken", data?.data?.refresh);
      storeToken("deviceId", data?.data?.device_id);
      storeToken("onboarding_steps", userData?.onboarding_steps);
      storeToken("gopremiummodal", "false");

      setIsPremium(userData?.is_premium_member);

      if (data?.data?.access_expires) {
        const expiry = dayjs.unix(data.data.access_expires).toISOString();
        storeToken("tokenExpiryTime", expiry);
      }

      login(userData);
      AlertNotification("success", data?.message);

      const step = userData?.onboarding_steps;

      if (userData?.gender === null) {
        navigate("/create", {
          state: {
            userData: {
              name: userData?.name,
              profileFor: userData?.profile_created_for,
            },
            showAccountFor: true,
          },
        });
      } else if (typeof step === "number") {
        if (step === 7) {
          navigate("/onboard/upload", { replace: true });
        } else if (step <= 7) {
          navigate(`/onboard#${step}`, { replace: true });
        } else {
          navigate("/explore", { replace: true });
        }
      } else {
        navigate("/explore", { replace: true });
      }
    },
  });
};

// Login with otp
export const useLoginWithOtp = (options = {}) => {
  return useMutation({
    mutationFn: loginWithOtp,
    onSuccess: (data, variables, context) => {
      AlertNotification("success", data.message);

      // Call external onSuccess if passed
      options.onSuccess?.(data, variables, context);
    },
  });
};

// Login Otp verification
export const useOtpVerification = () => {
  const { login } = useAuth();
  const { setIsPremium } = usePremium();

  return useMutation({
    mutationFn: ({ payload, fcmToken }) =>
      loginOtpVerification(payload, fcmToken),
    onSuccess: (data) => {
      storeToken("accessToken", data.data.access);
      storeToken("refreshToken", data.data.refresh);
      storeToken("deviceId", data.data.device_id);
      storeToken("onboarding_steps", data?.data?.user?.onboarding_steps);
      storeToken("gopremiummodal", "false");
      setIsPremium(data.user?.is_premium_member);
      if (data.data.access_expires) {
        const expiry = dayjs.unix(data.data.access_expires).toISOString();
        storeToken("tokenExpiryTime", expiry);
      }

      login(data.data.user);
      AlertNotification("success", data.message);
    },
  });
};

// landing register
export const usePreRegister = (options = {}) => {
  return useMutation({
    mutationFn: preRegister,
    onSuccess: (data, variables, context) => {
      AlertNotification("success", data.message);
      options.onSuccess?.(data, variables, context);
    },
  });
};

// landing register otp verification
export const usePreRegisterOtpVerify = (options = {}) => {
  return useMutation({
    mutationFn: preRegisterOtpVerify,
    onSuccess: (data, variables, context) => {
      storeToken("accessToken", data.data.access_token);
      AlertNotification("success", data.message);
      options.onSuccess?.(data, variables, context);
    },
  });
};

// create account
export const useCreateAccount = (options = {}) => {
  return useMutation({
    mutationFn: createAccount,
    onSuccess: (data, variables, context) => {
      storeToken("accessToken", data.data.access);
      storeToken("deviceId", data.data.device_id);
      AlertNotification("success", data.message);
      options.onSuccess?.(data, variables, context);
    },
  });
};

// Account for
export const useAccountFor = (options = {}) => {
  const { login } = useAuth();
  const { setIsPremium } = usePremium();
  return useMutation({
    mutationFn: AccountFor,
    onSuccess: (data, variables, context) => {
      storeToken("accessToken", data.data.access);
      storeToken("refreshToken", data.data.refresh);
      storeToken("deviceId", data.data.device_id);
      storeToken("onboarding_steps", data?.data?.user?.onboarding_steps);
      storeToken("gopremiummodal", "false");
      setIsPremium(data.data.user?.is_premium_member);

      if (data.data.access_expires) {
        const expiry = dayjs.unix(data.data.access_expires).toISOString();
        storeToken("tokenExpiryTime", expiry);
      }
      login(data.data.user);

      AlertNotification("success", data.message);
      options.onSuccess?.(data, variables, context);
    },
  });
};

export const useVerifyToken = () => {
  return useQuery({
    queryKey: ["verify-token"],
    queryFn: verifyToken,
    retry: false,
    refetchOnWindowFocus: false,
  });
};

export const useRefreshToken = (options = {}) => {
  const { login } = useAuth();

  return useMutation({
    mutationFn: refreshToken,
    ...options,
    onSuccess: (response) => {
      const data = response?.data;

      if (data?.access) {
        storeToken("accessToken", data.access);
      }

      if (data?.refresh) {
        storeToken("refreshToken", data.refresh);
      }

      if (data.access_expires) {
        const expiry = dayjs.unix(data.access_expires).toISOString();
        storeToken("tokenExpiryTime", expiry);
      }

      if (data?.user) {
        login(data.user);
        storeToken("onboarding_steps", data.user?.onboarding_steps);
      }

      if (options?.onSuccess) options.onSuccess(response);
    },
  });
};

// Logout

export const useLogout = (options = {}) => {
  const { logout } = useAuth();

  return useMutation({
    mutationFn: logoutApi,

    onSuccess: async (data, variables, context) => {
      // ✅ get user BEFORE clearing storage
      const user = JSON.parse(localStorage.getItem("user") || "{}");

      console.log(user, "user");

      // ✅ update firebase presence
      if (user?.aec_id) {
        await set(ref(rtdb, `/status/${user.aec_id}`), {
          online: false,
          lastChanged: Date.now(),
        });
      }

      // ✅ now clear app state
      logout(); // this clears user + tokens

      storeToken("gopremiummodal", "false");

      AlertNotification("success", data.message || "Logged out successfully");

      options?.onSuccess?.(data, variables, context);
    },
  });
};

export const useGoogleLogin = () => {
  return useMutation({
    mutationFn: googleLogin,
  });
};
